import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import {
  requestNotificationPermission,
  subscribeToPushNotifications,
  getNotificationPermission,
  showLocalNotification,
} from '../utils/notifications';
import { Globe, Bell, Lock, User, ArrowLeft, Eye, EyeOff, MessageCircle } from 'lucide-react';
import { useDialog } from '../components/Dialog';
import { openKakaoChat } from '../utils/kakaoAuth';
import api from '../utils/api';
import './SettingsPage.css';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, language, changeLanguage, logout } = useAuth();
  const { t } = useTranslation(language);
  const { showConfirm, showSuccess, showError, DialogComponent } = useDialog();
  const [notificationEnabled, setNotificationEnabled] = useState(
    getNotificationPermission() === 'granted'
  );
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setShowLanguageModal(false);
  };

  const handleNotificationToggle = async () => {
    if (!notificationEnabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotificationEnabled(true);
        await subscribeToPushNotifications();
        showLocalNotification(t('notificationEnabled'), {
          body: t('notificationEnabledBody'),
        });
      }
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showError(t('allFieldsRequired') || 'All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError(t('passwordsDoNotMatch') || 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showError(t('passwordMinLength') || 'Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      showSuccess(t('passwordChangedSuccessfully') || 'Password changed successfully');
      setShowChangePasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswords({ current: false, new: false, confirm: false });
    } catch (error) {
      console.error('Failed to change password:', error);
      const errorMsg = error.response?.data?.message || error.message;
      showError(errorMsg === 'Current password is incorrect'
        ? (t('currentPasswordIncorrect') || 'Current password is incorrect')
        : (t('cannotChangePassword') || 'Cannot change password'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    console.log('[Settings] Logout button clicked');
    showConfirm(
      t('logoutConfirm'),
      async () => {
        console.log('[Settings] User confirmed logout');
        await logout();
        console.log('[Settings] Logout completed');
      }
    );
  };

  const languages = [
    { code: 'vi', label: '🇻🇳 Tiếng Việt', shortLabel: 'Tiếng Việt' },
    { code: 'en', label: '🇬🇧 English', shortLabel: 'English' },
    { code: 'ko', label: '🇰🇷 한국어', shortLabel: '한국어' },
  ];

  return (
    <div className="settings-page">
      <DialogComponent />
      {/* Header */}
      <div className="page-header">
        <div className="header-nav">
          <button
            className="header-back"
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 'normal',
              lineHeight: '1',
            }}
          >
            ←
          </button>
          <h1 className="page-title">{t('settings')}</h1>
          <div className="header-spacer"></div>
        </div>
      </div>

      {/* Profile Card */}
      <div className="profile-card">
        <div className="user-info">
          <div className="user-avatar">
            <User size={32} />
          </div>
          <div className="user-details">
            <p className="user-name">{user?.name || user?.username}</p>
            <p className="user-email">{user?.email}</p>
            <p className="user-role">{t('ctvRole')}</p>
            <p className="user-code">{t('userCode')}: {user?.code || user?.id}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {/* Account Settings Group */}
        <div className="settings-section">
          <h2 className="section-title">{t('accountSection')}</h2>
          <div className="card">
            <ul className="settings-list">
              <li>
                <button
                  className="setting-item setting-item-button"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  <div className="setting-info">
                    <Lock className="setting-icon" size={24} />
                    <span className="setting-label">{t('changePassword')}</span>
                  </div>
                  <span className="setting-arrow">›</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* App Settings Group */}
        <div className="settings-section">
          <h2 className="section-title">{t('appSection')}</h2>
          <div className="card">
            <ul className="settings-list">
              <li>
                <div className="setting-item">
                  <div className="setting-info">
                    <Bell className="setting-icon" size={24} />
                    <span className="setting-label">{t('notifications')}</span>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationEnabled}
                      onChange={handleNotificationToggle}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </li>
              <li>
                <button
                  className="setting-item setting-item-button"
                  onClick={() => setShowLanguageModal(true)}
                >
                  <div className="setting-info">
                    <Globe className="setting-icon" size={24} />
                    <span className="setting-label">{t('languageLabel')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="setting-value">
                      {languages.find(l => l.code === language)?.shortLabel || 'Tiếng Việt'}
                    </span>
                    <span className="setting-arrow">›</span>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Support Section */}
        <div className="settings-section">
          <h2 className="section-title">{t('support') || 'Support'}</h2>
          <div className="card">
            <ul className="settings-list">
              <li>
                <button
                  className="setting-item setting-item-button kakao-chat-btn"
                  onClick={() => openKakaoChat()}
                >
                  <div className="setting-info">
                    <MessageCircle className="setting-icon" size={24} color="#FEE500" />
                    <span className="setting-label">{t('contactViaKakao') || 'Contact via Kakao'}</span>
                  </div>
                  <span className="setting-arrow">›</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Logout Button */}
        <div className="logout-section">
          <button className="logout-button" onClick={handleLogout}>
            {t('logout')}
          </button>
        </div>

        {/* Hidden App Info */}
        <div className="app-info">
          <p className="app-version">{t('appVersion')} 1.0.0</p>
          <p className="app-copyright">{t('appCopyright')}</p>
        </div>
      </div>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="modal-overlay" onClick={() => setShowLanguageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('selectLanguage')}</h3>
              <button className="modal-close" onClick={() => setShowLanguageModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-option ${language === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className="language-label">{lang.label}</span>
                  {language === lang.code && <span className="check-icon">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('changePassword')}</h3>
              <button className="modal-close" onClick={() => setShowChangePasswordModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  {t('currentPassword') || 'Current Password'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6B7280'
                    }}
                  >
                    {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  {t('newPassword') || 'New Password'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6B7280'
                    }}
                  >
                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  {t('confirmNewPassword') || 'Confirm New Password'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6B7280'
                    }}
                  >
                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: changingPassword ? '#9CA3AF' : '#3366ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: changingPassword ? 'not-allowed' : 'pointer'
                }}
              >
                {changingPassword ? (t('changing') || 'Changing...') : (t('changePassword') || 'Change Password')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
